
import Vue from "vue";
import { mapState } from "vuex";

interface Row {
  label: string;
  values: string[];
}

export default Vue.extend({
  computed: {
    ...mapState("app", { isCN: "isCN", isMobile: "isMobile" }),
    ...mapState("assets", { oss: "oss", url: "url" }),
    heroImage(): string {
      return this.media(
        "am_infinity_100_r2_01_hero_pc.webp",
        "am_infinity_100_r2_01_hero_mobile.webp"
      );
    },
    features(): Array<{
      key: string;
      desktop: string;
      mobile?: string;
      upgrade?: boolean;
    }> {
      return [
        {
          key: "batteryFeature",
          desktop: "am_infinity_100_r2_02_feature_battery.webp"
        },
        {
          key: "weightFeature",
          desktop: "am_infinity_100_r2_03_feature_weight.webp"
        },
        {
          key: "sensorFeature",
          desktop: "am_infinity_100_r2_04_feature_sensor.webp",
          upgrade: true
        },
        {
          key: "engineFeature",
          desktop: "am_infinity_100_r2_05_feature_engine.webp",
          upgrade: true
        },
        {
          key: "waterproofFeature",
          desktop: "am_infinity_100_r2_06_feature_waterproof.webp",
          upgrade: true
        },
        {
          key: "peiFeature",
          desktop: "am_infinity_100_r2_07_feature_pei.webp",
          upgrade: true
        },
        {
          key: "modeFeature",
          desktop: "am_infinity_100_r2_08_feature_mode_pc.webp",
          mobile: "am_infinity_100_r2_08_feature_mode_mobile.webp",
          upgrade: true
        },
        {
          key: "driverFeature",
          desktop: "am_infinity_100_r2_09_feature_driver_pc.webp",
          mobile: "am_infinity_100_r2_09_feature_driver_mobile.webp",
          upgrade: true
        }
      ];
    },
    outcomeItems(): Array<{ title: string; text: string }> {
      return [1, 2, 3].map(index => ({
        title: String(this.$t(`AMInfinity100R2.outcome${index}Title`)),
        text: String(this.$t(`AMInfinity100R2.outcome${index}Text`))
      }));
    },
    latencyRows(): Array<{
      key: string;
      label: string;
      value: string;
      delay: string;
      width: string;
      tone: string;
    }> {
      return [
        {
          key: "am8000",
          label: String(this.$t("AMInfinity100R2.latencyAmLabel")),
          value: "8000 Hz",
          delay: "0.184",
          width: "17%",
          tone: "am"
        },
        {
          key: "flagship8000a",
          label: String(this.$t("AMInfinity100R2.latencyFlagshipLabel")),
          value: "8000 Hz",
          delay: "0.291",
          width: "27%",
          tone: "green"
        },
        {
          key: "flagship8000b",
          label: String(this.$t("AMInfinity100R2.latencyFlagshipLabel")),
          value: "8000 Hz",
          delay: "0.461",
          width: "43%",
          tone: "green"
        },
        {
          key: "industry8000",
          label: String(this.$t("AMInfinity100R2.latencyIndustryLabel")),
          value: "8000 Hz",
          delay: "0.807",
          width: "75%",
          tone: "yellow"
        },
        {
          key: "flagship1000",
          label: String(this.$t("AMInfinity100R2.latencyFlagshipLabel")),
          value: "1000 Hz",
          delay: "0.934",
          width: "86%",
          tone: "green"
        },
        {
          key: "industry1000",
          label: String(this.$t("AMInfinity100R2.latencyIndustryLabel")),
          value: "1000 Hz",
          delay: "1.083",
          width: "100%",
          tone: "yellow"
        }
      ];
    },
    products(): Array<{ name: string; image: string }> {
      return [
        {
          name: "AM Infinity .97",
          image: "am_infinity_100_r2_18_guide_97.webp"
        },
        {
          name: "AM Infinity .100 R2",
          image: "am_infinity_100_r2_18_guide_100_r2.webp"
        },
        {
          name: "AM Infinity .100 R1",
          image: "am_infinity_100_r2_18_guide_100_r1.webp"
        }
      ];
    },
    comparisonRows(): Row[] {
      return [
        {
          label: String(this.$t("AMInfinity100R2.comparison.shape")),
          values: ["类毒蝰，低背收腰", "类 GPW，饱满支撑", "类 GPW，饱满支撑"]
        },
        {
          label: String(this.$t("AMInfinity100R2.comparison.hand")),
          values: ["中小手", "中大手", "中大手"]
        },
        {
          label: String(this.$t("AMInfinity100R2.comparison.sensor")),
          values: ["PAW 3950", "PAW 3955", "PAW 3950"]
        },
        {
          label: String(this.$t("AMInfinity100R2.comparison.engine")),
          values: ["Real8K Engine V2", "Real8K Engine V2", "Real8K Engine V1"]
        },
        {
          label: String(this.$t("AMInfinity100R2.comparison.endurance")),
          values: ["30H+", "30H+", "17H+"]
        },
        {
          label: String(this.$t("AMInfinity100R2.comparison.water")),
          values: ["纳米防水涂层", "纳米防水涂层 Plus", "仅赤橙配色有防水涂层"]
        },
        {
          label: String(this.$t("AMInfinity100R2.comparison.mode")),
          values: ["无", "有", "无"]
        },
        {
          label: String(this.$t("AMInfinity100R2.comparison.pei")),
          values: ["标配可选", "标配可选", "无"]
        }
      ];
    },
    faqItems(): Array<{ question: string; answer: string }> {
      return [1, 2, 3, 4, 5].map(index => ({
        question: String(this.$t(`AMInfinity100R2.faq${index}Question`)),
        answer: String(this.$t(`AMInfinity100R2.faq${index}Answer`))
      }));
    },
    specRows(): Row[] {
      return [
        "weight",
        "material",
        "shape",
        "sensor",
        "engine",
        "switch",
        "connection",
        "endurance",
        "buttons",
        "battery",
        "size"
      ].map(key => ({
        label: String(this.$t(`AMInfinity100R2.spec.${key}Label`)),
        values: [String(this.$t(`AMInfinity100R2.spec.${key}Value`))]
      }));
    },
    packingItems(): string[] {
      return [1, 2, 3, 4, 5, 6, 7, 8].map(index =>
        String(this.$t(`AMInfinity100R2.packing${index}`))
      );
    },
    serviceItems(): Array<{ value: string; label: string }> {
      return [1, 2, 3].map(index => ({
        value: String(this.$t(`AMInfinity100R2.service${index}Value`)),
        label: String(this.$t(`AMInfinity100R2.service${index}Label`))
      }));
    }
  },
  methods: {
    asset(name: string): string {
      return this.oss.am_infinity_100_r2 + name;
    },
    media(desktop: string, mobile?: string): string {
      return this.asset(this.isMobile && mobile ? mobile : desktop);
    }
  }
});
